import { appURLs } from "@/app/app-urls.mjs";
import type { GameDefinition } from "@/app/game-definition.mjs";
import { SUB_CATEGORIES } from "@/game-destiny2/constants/category.mjs";
import { GAME_SYMBOL_DESTINY2 } from "@/game-destiny2/definition-symbol.mjs";
import type { ProfileLoadout } from "@/game-destiny2/models/bungie-character-loadout.mjs";
import type { Profile } from "@/game-destiny2/models/destiny2-bungie-profile.mjs";
import type { CharacterModel } from "@/game-destiny2/models/destiny2-characters.mjs";
import type { Match } from "@/game-destiny2/models/destiny2-match.mjs";
import type { MatchListItem } from "@/game-destiny2/models/destiny2-match-list.mjs";
import type { GuardianStats } from "@/game-destiny2/models/destiny2-player-guardian-stats.mjs";
import type { MapStats } from "@/game-destiny2/models/destiny2-player-map-stats.mjs";
import type { WeaponStats } from "@/game-destiny2/models/destiny2-player-weapon-stats.mjs";
import type { Seasons } from "@/game-destiny2/models/destiny2-seasons.mjs";
import type {
  Activity,
  Armor,
  Artifact,
  Mod,
  Perk,
  Stat,
  Subclass,
  Weapon,
} from "@/game-destiny2/models/model-wiki.mjs";
import routes from "@/game-destiny2/routes.mjs";
import type { BungieTokenMap } from "@/game-destiny2/utils/token-model.mjs";
import GameIconShapeDestiny2 from "@/inline-assets/game-destiny2-icon.svg";
import GameIconBoxDestiny2 from "@/inline-assets/game-icon-box-destiny2.svg";

export { GAME_SYMBOL_DESTINY2 };

export const GameDestiny2 = {
  gameSymbol: GAME_SYMBOL_DESTINY2,
  friendlyName: ["common:games.destiny2.short", "Destiny 2"],
  fullName: ["common:games.destiny2.long", "Destiny 2"],
  shortName: "destiny2" as const,
  backendName: "DESTINY2" as const,
  gameColor: "#EFBA52",

  iconShape: GameIconShapeDestiny2,
  iconBox: GameIconBoxDestiny2,
  iconBadge: GameIconBoxDestiny2,
  icon: `${appURLs.CDN_PLAIN}/blitz/ui/images/icons/games/small/destiny2.svg`,
  imageKeyart: `${appURLs.CDN}/blitz/ui/img/game-cover/de2-min.webp`,
  imageBanner: `${appURLs.CDN}/blitz/ui/img/game-bg/de2-39ed3.webp`,

  isActive: true,
  preferFriendlyName: false as const,

  IPC: () => import("@/game-destiny2/handle-messages.mjs"),
  settings: () => import("@/game-destiny2/Accounts.jsx"),
  navLinks: [
    {
      href: `/destiny2/loadout-builder`,
      text: ["destiny2:loadoutBuilder.title", "Loadout Builder"],
      tag: ["common:beta", "Beta"],
    },
    {
      href: `/destiny2/database`,
      text: ["common:navigation.database", "Database"],
      menu: [
        [
          {
            category: ["common:wiki.weapons", "Weapons"],
            items: [
              {
                text: ["common:weaponCategories.all", "All Weapons"],
                href: "/destiny2/database/weapons",
              },
              {
                text: ["gen-destiny2:rarity.exotic", "Exotic"],
                href: "/destiny2/database/weapons?rarity=exotic",
              },
              ...[
                SUB_CATEGORIES["auto-rifle"],
                SUB_CATEGORIES["hand-cannon"],
                SUB_CATEGORIES["pulse-rifle"],
                SUB_CATEGORIES["scout-rifle"],
                SUB_CATEGORIES["fusion-rifle"],
              ].map((c) => ({
                text: c.label,
                href: `/destiny2/database/weapons?category=${c.slug}`,
              })),
            ],
          },
        ],
        [
          {
            category: ["common:wiki.armor", "Armor"],
            items: [
              {
                text: ["common:wiki.allArmor", "All Armor"],
                href: "/destiny2/database/armor",
              },
              {
                text: ["gen-destiny2:rarity.exotic", "Exotic"],
                href: "/destiny2/database/armor?rarity=exotic",
              },
              ...[
                SUB_CATEGORIES.helmets,
                SUB_CATEGORIES.arms,
                SUB_CATEGORIES.chest,
                SUB_CATEGORIES.legs,
                SUB_CATEGORIES["class-items"],
              ].map((c) => ({
                text: c.label,
                href: `/destiny2/database/armor?category=${c.slug}`,
              })),
            ],
          },
        ],
      ],
    },
    {
      href: `/destiny2/faq`,
      text: ["destiny2:faq.howItWorks", "How It Works"],
    },
  ],
  routes,
  meta: {
    bg: `${appURLs.CDN}/blitz/ui/img/game-bg/de2-39ed3.webp`,
    articles: [
      {
        createdAt: new Date("Aug 13, 2024 18:00:00"),
        title: ["welcome:destiny2.itemDatabase", "Item Database"],
        subtitle: [
          "welcome:destiny2.itemDatabaseSubtitle",
          "Dive into key information and stats on every weapon and armor piece in the game!",
        ],
        link: {
          href: "/destiny2/database/weapons",
          text: ["common:navigation.viewItemDatabase", "View Item Database"],
        },
        background: `${appURLs.CDN}/blitz/ui/img/homepage/destiny2/item-deebee.webp`,
        image: `${appURLs.CDN}/blitz/ui/img/homepage/destiny2/item-deebee.webp`,
      },
      {
        createdAt: new Date("Aug 13, 2024 18:00:00"),
        title: ["welcome:destiny2.itemManager", "Item Manager"],
        subtitle: [
          "welcome:destiny2.itemManagerSubtitle",
          "Coming Soon: Dive into the Blitz Item Manager, where you can optimize the perfect loadout for any situaiton!",
        ],
        background: `${appURLs.CDN}/blitz/ui/img/homepage/destiny2/item-manners.webp`,
        image: `${appURLs.CDN}/blitz/ui/img/homepage/destiny2/item-manners.webp`,
      },
    ],
    features: [
      {
        title: ["destiny2.features.weaponsDatabase", "Weapon Database"],
        description: ["", ""],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/destiny2/feature-weapons-database.webp`,
      },
      {
        title: ["destiny2.features.armorsDatabase", "Armor Database"],
        description: ["", ""],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/destiny2/feature-armors-database.webp`,
      },
      {
        title: ["eft.features.profile.title", "Profile Page"],
        description: ["", ""],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/destiny2/feature-profile.webp`,
      },
      {
        title: ["eft.features.postMatchInsights.title", "Post Match Insights"],
        description: ["", ""],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/destiny2/feature-postmatch.webp`,
      },
      {
        title: ["welcome:destiny2.itemManager", "Item Manager"],
        description: ["", ""],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/destiny2/feature-item-manager.webp`,
      },
    ],
  },
} satisfies GameDefinition<typeof GAME_SYMBOL_DESTINY2>;

declare module "@/__main__/app-state.mjs" {
  interface AppState {
    destiny2: {
      oauth: BungieTokenMap;
      profiles: Record<string, Profile>;
      matchList: Record<string, MatchListItem[]>;
      match: Record<string, Match>;
      characters: Record<string, Record<string, CharacterModel>>;
      seasons: Seasons;
      // By character
      mapStats: Record<string, MapStats[]>;
      weaponStats: Record<string, WeaponStats[]>;
      // By character by season
      guardianStats: Record<string, Record<string, GuardianStats>>;
      showInfoModal: { show: boolean };
      wiki: {
        version: string[];
        weapons: Record<number | string, Weapon>;
        perks: Record<number, Perk>;
        plugLists: Record<number, number[]>;
        lore: Record<number, string>;
        mods: Record<number, Mod>;
        stats: Record<number, Stat>;
        armor: Record<number, Armor>;
        i18n: Record<string, string>;
        activities: Record<string, Activity>;
        artifacts: Record<number, Artifact>;
        subclasses: {
          subclasses?: Record<number, Subclass>;
          mods?: Record<number, Mod>;
        };
      };
      characterLoadout: Record<string, Partial<ProfileLoadout>>;
      // locally persisted
      selectedCharacterId: string;
    };
  }

  interface VolatileState {
    d2InvUpdate?: boolean;
    d2Polling?: boolean;
    d2ApiUnavailable?: boolean;
    d2GameState?: {
      currentCharacter: {
        characterId: string;
        currentActivityHash: number;
        currentActivityModeHash: number;
        dateActivityStarted: Date;
      };
      isRefreshing: boolean;
      // timestamp of next expected refresh
      nextRefresh: number;
    };
  }
}

export default GameDestiny2;
